import Phaser from 'phaser';

/**
 * Pitch-contour arrow glyphs for tonal game modes (see specs/tonal-modes.md §3.1/§3.4).
 *
 * Each tone is a normalized polyline in a [0,1]×[0,1] box where x runs left→right
 * (start→end of the syllable) and y is pitch with 0 = high (top) and 1 = low (bottom). An
 * arrowhead is drawn at the END of the path so the glyph reads as a directional arrow:
 *
 *   mid     →  straight arrow   (level)
 *   high    ↗  up arrow         (rises to high)
 *   low     ↘  down arrow       (falls to low)
 *   falling ⌐\ falling curve    (peak, then down)
 *   rising  \/ rising curve     (dip, then up)
 *
 * Drawn with scene.add.graphics() — asset-free, matching the project's house style
 * (Starfield, synthesized audio). No tone *logic* lives here; the banner / help panel just
 * look up BoardRule.contour (or PackClass.contour) and draw the named glyph.
 */
const CONTOURS: Record<string, Array<[number, number]>> = {
  // สามัญ — level: a straight horizontal arrow.
  mid: [[0, 0.5], [1, 0.5]],
  // ตรี — high: a straight arrow pointing up.
  high: [[0, 0.8], [1, 0.2]],
  // เอก — low: a straight arrow pointing down.
  low: [[0, 0.2], [1, 0.8]],
  // โท — falling: rises a little, then falls sharply (arrowhead down).
  falling: [[0, 0.45], [0.4, 0.18], [1, 0.85]],
  // จัตวา — rising: dips, then rises sharply (arrowhead up).
  rising: [[0, 0.5], [0.42, 0.82], [1, 0.13]],
};

const GLYPH_W = 52;
const GLYPH_H = 36;
const STROKE = 0xffd166; // matches the banner text colour
const HEAD_LEN = 11;     // arrowhead barb length (px, at scale 1)
const HEAD_ANGLE = 0.5;  // arrowhead half-spread (radians, ~28°)

/** Returns true if `name` is a known contour glyph. */
export function hasContour(name: string | undefined): boolean {
  return typeof name === 'string' && name in CONTOURS;
}

/**
 * Builds a contour arrow glyph centred on its own origin (0,0), ready to position next to the
 * banner. Returns null for an unknown name so callers can skip drawing gracefully.
 */
export function drawContour(scene: Phaser.Scene, name: string): Phaser.GameObjects.Container | null {
  const points = CONTOURS[name];
  if (!points) return null;

  const px = (nx: number) => (nx - 0.5) * GLYPH_W;
  const py = (ny: number) => (ny - 0.5) * GLYPH_H;

  const g = scene.add.graphics();
  g.lineStyle(5, STROKE, 1);
  g.beginPath();
  points.forEach(([nx, ny], i) => {
    if (i === 0) g.moveTo(px(nx), py(ny));
    else g.lineTo(px(nx), py(ny));
  });
  g.strokePath();

  // Arrowhead at the path end, pointing along the final segment's direction.
  const [ex, ey] = points[points.length - 1];
  const [bx, by] = points[points.length - 2];
  const tipX = px(ex);
  const tipY = py(ey);
  const angle = Math.atan2(py(ey) - py(by), px(ex) - px(bx));
  for (const sign of [-1, 1]) {
    const a = angle + Math.PI + sign * HEAD_ANGLE;
    g.beginPath();
    g.moveTo(tipX, tipY);
    g.lineTo(tipX + Math.cos(a) * HEAD_LEN, tipY + Math.sin(a) * HEAD_LEN);
    g.strokePath();
  }

  return scene.add.container(0, 0, [g]);
}
